<template>
    <div v-if="campaignInfo" class="wheel-campaign" ref="wheelCampaignRef">
        <div class="wheel-campaign__main">
            <div class="wheel-campaign__main-upper">
                <div class="wheel-campaign__main-note">
                    <DsSmileStatic
                        :faceType="faceType"
                        :facePosition="FacePosition.RightDown"
                        :responsive="false"
                        class="wheel-campaign__main-note-smile"
                    ></DsSmileStatic>
                    <div class="wheel-campaign__main-note-about">
                        <div class="wheel-campaign__main-note-text">
                            <span v-html="promoText1"></span>&#32;<span class="wheel-campaign__main-note-goal">{{
                                goalSumFormatted
                            }}</span
                            >&#32;<span v-html="campaignInfo.texts?.promoText2"></span>&#32;<span
                                class="wheel-campaign__main-note-bold"
                                v-html="campaignInfo.texts?.promoText3"
                            ></span
                            >.
                        </div>
                        <MobileAppLink
                            :href="campaignInfo.texts?.promoLink"
                            :openInNewTab="true"
                            class="wheel-campaign__main-note-link"
                            @click="moreInfoClick()"
                        >
                            Více informací
                            <DsIcon name="help-thin" swapBreakpoint="sm2"></DsIcon>
                        </MobileAppLink>
                    </div>
                </div>
            </div>

            <div class="wheel-campaign__main-lower" ref="collectedSumInfoRef">
                <div class="wheel-campaign__progress-collected" v-show="isCollectedSumInViewport">
                    <span class="wheel-campaign__progress-text"> Již jsme vybrali </span>
                    <span class="wheel-campaign__progress-num">
                        {{ collectedSumFormatted }}
                    </span>
                </div>
                <div class="wheel-campaign__progress-bar">
                    <div class="wheel-campaign__progress-fill" :style="progressBarStyle"></div>
                </div>
            </div>
        </div>

        <div class="wheel-campaign__side" v-show="!isGoalCompleted">
            <div class="wheel-campaign__side-note">
                <div class="wheel-campaign__side-note-card">
                    <WebpImage
                        class="wheel-campaign__side-note-coin"
                        src="/frontend/kasa-loyalty/img/widgets/wheel-campaign/Coin.png"
                        alt="zlatá mince"
                    >
                    </WebpImage>
                    <div class="wheel-campaign__side-note-text">Za každé vaše zatočení věnuje Sazka 1&nbsp;Kč.</div>
                </div>
                <MobileAppLink
                    href="/sazka-klub/zatocte-si-kolem"
                    class="wheel-campaign__side-note-link"
                    @click="spinWheelClick()"
                >
                    {{ spinWheelButtonText }}
                    <DsIcon name="arrow-right-thin" swapBreakpoint="md"></DsIcon>
                </MobileAppLink>
            </div>

            <WebpImage
                class="wheel-campaign__side-wheel"
                src="/frontend/kasa-loyalty/img/widgets/wheel-campaign/campaign-wheel.png"
                alt="kolo štěstí"
            >
            </WebpImage>
        </div>

        <div class="wheel-campaign__side-completed" v-show="isGoalCompleted">
            <div class="wheel-campaign__thanks-note">
                <span class="wheel-campaign__thanks-note-text">Děkujeme! </span>
                <WebpImage
                    class="wheel-campaign__thanks-note-heart"
                    src="/frontend/kasa-loyalty/img/widgets/wheel-campaign/Heart.png"
                    alt="emoji srdce"
                >
                </WebpImage>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed, reactive, ref, nextTick } from "vue";
import gsap from "gsap";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import DsSmileStatic from "@sazka/design-system/src/smiles//ds-smile-static.vue";
import MobileAppLink from "@sazka/mobile-apps/components/mobile-app-link.vue";
import {
    DsSmileStaticFacePosition as FacePosition,
    DsSmileStaticFaceType as FaceType,
} from "@sazka/design-system/src/smiles//ds-smile-static-types";
import { getActiveWheelCampaign } from "@sazka/utils/api/wheel-campaign-api";
import { WheelCampaign } from "@sazka/utils/models/wheel-campaign";
import { StringUtils } from "@sazka/utils/string-utils";
import { onViewportEnter } from "@sazka/utils/viewport-enter";
import {
    dataLayerCharityBannerImpression,
    dataLayerCharityBannerClickMoreInfo,
    dataLayerCharityBannerClickSpinWheel,
} from "./wof-analytics";

const animationDuration = 1.6;
const bannerPosition = "middle";

const wheelCampaignRef = ref(null);
const campaignInfo = ref<WheelCampaign>(null);
const isCollectedSumInViewport = ref(false);
const collectedSumInfoRef = ref<HTMLElement>(null);
const snap = gsap.utils.snap(1);

const tweenedSum = reactive({
    number: 0,
});
const tweenedProgress = reactive({
    number: 0,
});

const progressBarStyle = computed(() => "width: " + tweenedProgress.number + "%");
const goalProgressPercentage = computed(() => {
    if (!campaignInfo.value || campaignInfo.value.currentValue == 0 || campaignInfo.value.targetValue == 0) return 0;
    if (campaignInfo.value.currentValue / campaignInfo.value.targetValue >= 1) return 100;
    return (100 * campaignInfo.value.currentValue) / campaignInfo.value.targetValue;
});
const isGoalCompleted = computed(() => goalProgressPercentage.value >= 100);
const goalSumFormatted = computed(() => StringUtils.toCurrencyString(campaignInfo.value.targetValue));
const collectedSumFormatted = computed(
    () => StringUtils.toCurrencyString(snap(tweenedSum.number)) + (isGoalCompleted.value ? "!" : "")
);
const promoText1 = computed(() =>
    isGoalCompleted.value ? "Úžasné! Pomohli jste nám vybrat" : campaignInfo.value?.texts?.promoText1
);
const faceType = computed(() => (isGoalCompleted.value ? FaceType.Stars : FaceType.Hearts));
const spinWheelButtonText = ref("Zatočit Kolem Štěstí");

const getBannerTextForDataLayer = () =>
    campaignInfo.value?.texts.wheelPromoText1 + " " + campaignInfo.value?.texts.wheelPromoText2;

const moreInfoClick = () => {
    dataLayerCharityBannerClickMoreInfo();
};

const spinWheelClick = () => {
    dataLayerCharityBannerClickSpinWheel(campaignInfo.value.id.toString(), spinWheelButtonText.value, bannerPosition);
};

const setupActiveCampaign = async () => {
    try {
        campaignInfo.value = await getActiveWheelCampaign();
    } catch (error) {
        console.log("activeCampaign", error);
    }

    if (campaignInfo.value) {
        nextTick(() => {
            const hash = window.location.hash.substring(1);
            if (hash == "dobrokolo") {
                wheelCampaignRef.value.scrollIntoView(true);
            }

            onViewportEnter(collectedSumInfoRef.value).then(() => {
                isCollectedSumInViewport.value = true;
                gsap.to(tweenedSum, { duration: animationDuration, number: campaignInfo.value.currentValue ?? 0 });
                gsap.to(tweenedProgress, { duration: animationDuration, number: goalProgressPercentage.value });

                dataLayerCharityBannerImpression(
                    campaignInfo.value.id.toString(),
                    getBannerTextForDataLayer(),
                    bannerPosition
                );
            });
        });
    }
};

setupActiveCampaign();
</script>
